import { useCallback, useMemo, useState } from "react"
import { DateTime } from "luxon"
import { MenuItem } from "@mui/material"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { MRT_Row, MRT_ColumnDef } from "material-react-table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import useActiveFiltersCountAccounting from "../../../../../../shared/components/filters/accountingFiltersDrawer/useActiveFiltersCountAccounting"
import { PaymentMethodCell } from "../../../../../merchant/merchant/production/components/PaymentMethodCell"
import { ChannelTypeChip } from "../../../../../merchant/merchant/production/components/ChannelTypeChip"
import { fetchSupplierPosOrders, SupplierOrdersResponse, SupplierPOSQuery } from "../posApi"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import {
    AccountingFilterProps,
    AccountingFiltersDrawer,
    defaultAccountingFilterOptionsList,
} from "../../../../../../shared/components/filters/accountingFiltersDrawer/AccountingFiltersDrawer"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { CompaniesOrderItem } from "../../../../../company/reports/accounting/pos/posTypes"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { Receipt, ReceiptDialog } from "../../../../../receipt/pages/ReceiptDialog"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { SUPPLIER_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import { useExportPosOrders } from "../hooks/useExportPosOrders"
import { useSupplier } from "../../../../supplierHooks"
import {
    StandardDateRangeFilter,
    type DtDateRange,
} from "../../../../../../shared/components/table/StandardDateRangeFilter"

function transformData(data: SupplierOrdersResponse) {
    return data.orders
}

export function SupplierPOSOrders() {
    const { l10n } = useAppLocalization()
    const [receipt, setReceipt] = useState<Receipt>()

    const { supplierId } = useSupplier()

    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const [drawerOpen, setDrawerOpen] = useState(false)

    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<AccountingFilterProps>({
        ...defaultAccountingFilterOptionsList,
    })
    const [dateState, setDateState] = useState<DtDateRange>({
        startDate,
        endDate,
    })
    const { activeCount } = useActiveFiltersCountAccounting(filtersList)

    const supplierOrders = useApiQuery(fetchSupplierPosOrders, {
        queryName: SupplierPOSQuery.POS_ORDERS_FOR_SUPPLIER,
        dependencies: {
            supplierId,
            dateType,
            ...filtersList,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: dateState.startDate?.toISODate(),
            endDate: dateState.endDate?.toISODate(),
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(supplierOrders)

    const columns = useMemo<MRT_ColumnDef<CompaniesOrderItem>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-pos-table-header-order-id"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("supplier-pos-table-header-transaction-time"),
                    accessorKey: "created",
                },
                (row) => row.created,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-pos-table-header-merchant-name"),
                accessorKey: "sellerSupplierParty.name",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("supplier-pos-table-header-payment-channel"),
                accessorKey: "paymentDetails.channel.displayText",
                Cell: ({ row }) => <ChannelTypeChip channel={row.original.paymentDetails.channel} />,
            }),
            columnFactory.typeColumn({
                header: l10n.getString("supplier-pos-table-header-payment"),
                accessorKey: "paymentDetails.method",
                Cell: ({ row }) => <PaymentMethodCell paymentDetails={row.original.paymentDetails} />,
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("supplier-pos-table-header-amount"),
                    accessorKey: "amount.amount",
                },
                (row) => row.amount,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<CompaniesOrderItem>; closeMenu: () => void }) => [
            <MenuItem
                key={`show_receipt-${row.original.id}`}
                onClick={() => {
                    showReceipt(row.original)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="supplier-pos-table-table-button-show-receipt">
                    <span>Show receipt</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [],
    )

    function showReceipt(row: CompaniesOrderItem) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const exportOrder = useExportPosOrders({
        data: supplierOrders.data,
        startDate: dateState.startDate || DateTime.now(),
        endDate: dateState.endDate || DateTime.now(),
    })

    return (
        <>
            <LocalizedStrict id="supplier-pos-table-header-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_ACCOUNTING_POS_ORDERS}
                    title="POS"
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-pos">
                            <Breadcrumb>POS</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <SelectDateTypeFilter dateType={dateType} setDateType={setDateType} />
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                        <StandardExportButton
                            onClick={() => {
                                exportOrder()
                            }}
                        />
                    </StandardTableFilter>
                )}
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap>
                        <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                            <TableNumberOfItemsBox
                                data={table.getFilteredRowModel().rows}
                                isLoading={supplierOrders.isLoading}
                                title="Number of orders"
                            />
                        </LocalizedStrict>

                        <TableTotalAmountBox
                            data={table.getFilteredRowModel().rows}
                            isLoading={supplierOrders.isLoading}
                            accessor={(row) => row.original.amount}
                        />
                    </TableSummaryWrap>
                )}
            />
            {receipt && (
                <ReceiptDialog
                    open={receipt !== undefined}
                    orders={supplierOrders.data}
                    onClose={() => setReceipt(undefined)}
                    receipt={receipt}
                />
            )}

            <AccountingFiltersDrawer
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
